import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UsersThree, Buildings, CalendarBlank, ChatCircleDots, CurrencyDollar, Broom, Wrench, TrendUp } from '@phosphor-icons/react';

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="admin-loading">Loading platform data...</div>;

  const cards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: UsersThree, color: 'text-[#0052FF]', bg: 'bg-blue-50' },
    { label: 'Properties', value: stats?.total_properties || 0, icon: Buildings, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Platform Revenue', value: `$${(stats?.platform_revenue || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: CalendarBlank, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Guest Chats', value: stats?.total_chats || 0, icon: ChatCircleDots, color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { label: 'Cleaning Tasks', value: stats?.total_cleaning || 0, icon: Broom, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  const subs = stats?.subscriptions || {};

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Platform Overview</h1>
        <p className="text-sm text-zinc-500 mt-1">Monitor your HostCockpit platform health and growth</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200 bg-white mb-8" data-testid="admin-stats-grid">
        {cards.map((c, i) => (
          <div key={i} className="p-6 border-b border-r border-zinc-200" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`w-9 h-9 ${c.bg} flex items-center justify-center rounded-sm mb-3`}>
              <c.icon size={18} weight="duotone" className={c.color} />
            </div>
            <p className="text-2xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{c.value}</p>
            <p className="text-xs text-zinc-500 font-medium mt-1 uppercase tracking-wide">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Subscription Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-zinc-200" data-testid="subscription-breakdown">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Subscription Breakdown</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {[
                { name: 'Starter', count: subs.starter || 0, price: '$99/mo', color: 'bg-zinc-200' },
                { name: 'Pro', count: subs.pro || 0, price: '$149/mo', color: 'bg-[#0052FF]' },
                { name: 'Scale', count: subs.enterprise || 0, price: '$199/mo', color: 'bg-zinc-900' },
              ].map((plan, i) => {
                const total = (subs.starter || 0) + (subs.pro || 0) + (subs.enterprise || 0);
                const pct = total > 0 ? (plan.count / total * 100) : 0;
                return (
                  <div key={i} data-testid={`sub-plan-${plan.name.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-zinc-900">{plan.name}</span>
                        <span className="text-xs text-zinc-500">{plan.price}</span>
                      </div>
                      <span className="text-sm font-bold text-zinc-900">{plan.count} users</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className={`h-full ${plan.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-4 border-t border-zinc-100 flex items-center justify-between">
              <span className="text-xs text-zinc-500 font-medium uppercase tracking-wide">Monthly Recurring</span>
              <span className="text-lg font-black text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>
                ${((subs.starter || 0) * 99 + (subs.pro || 0) * 149 + (subs.enterprise || 0) * 199).toLocaleString()}/mo
              </span>
            </div>
          </div>
        </div>

        {/* Recent Signups */}
        <div className="bg-white border border-zinc-200" data-testid="recent-signups">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Recent Signups</h2>
          </div>
          {stats?.recent_users?.length > 0 ? (
            <div className="divide-y divide-zinc-100">
              {stats.recent_users.slice(0, 6).map((u, i) => (
                <div key={i} className="px-6 py-3 flex items-center justify-between" data-testid={`recent-user-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-bold text-zinc-600">
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900">{u.name}</p>
                      <p className="text-xs text-zinc-500">{u.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 font-bold ${u.subscription_plan === 'pro' ? 'bg-blue-50 text-blue-700' : u.subscription_plan === 'enterprise' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}>
                    {u.subscription_plan}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-sm text-zinc-500">No users yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
